import { useCityList } from 'apis/home';
import { getOrderDetailBatch } from 'apis/oms';
import { getProductUpdateInfoNew } from 'apis/pms';
import { STOCK_PLACE_MAP } from 'constant/stock-place';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useMemo } from 'react';
import getTimezoneByStockPlace from 'utils/getTimezoneByStockPlace';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { UnwrapPromise } from 'types/base';

dayjs.extend(utc);
dayjs.extend(timezone);

export type OrderDetailBatchReturnType = UnwrapPromise<
  ReturnType<typeof getOrderDetailBatch>
>[number];

export const InvoiceLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="w-[210mm] h-[297mm] bg-white p-[20mm]">{children}</div>
  );
};

export const getInvoiceDate = (order: OrderDetailBatchReturnType) => {
  return order.deliveryTime;
};

export const getOrderStockPlace = (order: OrderDetailBatchReturnType) =>
  order.orderItems[0]?.product?.stockPlace;

/** 按订单商品库存地时区格式化 Invoice 日期 */
export const formatInvoiceDate = (
  order: OrderDetailBatchReturnType,
  format = 'D/M/YYYY',
) => {
  const dateString = getInvoiceDate(order);
  if (!dateString) return '-';

  const timezoneName = getTimezoneByStockPlace(getOrderStockPlace(order));
  if (timezoneName) {
    return dayjs(dateString).tz(timezoneName).format(format);
  }

  return dayjs(dateString).format(format);
};

export const formatAmount = (amount?: number | null) => {
  const value = amount ?? 0;
  return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const useInvoiceRecipientAddress = (
  order: OrderDetailBatchReturnType,
) => {
  const { countryOptions } = useAppSelector(selectGlobalInfo);
  const { cityList } = useCityList(order.receiverProvince, 'en');

  const countryLabel = useMemo(
    () =>
      countryOptions.find((country) => country.value === order.receiverProvince)
        ?.nameI18n?.en ||
      order.receiverProvince ||
      '',
    [countryOptions, order.receiverProvince],
  );

  const cityLabel = useMemo(
    () =>
      cityList.find((city) => city.code === order.receiverCity)?.name ||
      order.receiverCity ||
      '',
    [cityList, order.receiverCity],
  );

  return {
    cityLabel,
    countryLabel,
  };
};

export const CustomerAddressBlock = ({
  order,
}: {
  order: OrderDetailBatchReturnType;
}) => {
  const { cityLabel, countryLabel } = useInvoiceRecipientAddress(order);

  return (
    <div>
      <div className="font-bold mb-2 text-xs">TO :</div>
      <div className="text-xs leading-[1.8]">
        {order.receiverName}
        <br />
        {order.receiverDetailAddress}
        <br />
        {cityLabel} {countryLabel}
        <br />
        {order.receiverPhone}
      </div>
    </div>
  );
};

export const InvoiceInfoBlock = ({
  order,
  invoiceNoLabel,
  showGstRegistration = true,
  showTerms = true,
}: {
  order: OrderDetailBatchReturnType;
  invoiceNoLabel: string;
  showGstRegistration?: boolean;
  showTerms?: boolean;
}) => {
  return (
    <div className="w-[220px] shrink-0 text-xs leading-[1.8] space-y-2 text-right">
      {showGstRegistration ? <div>GST Registration No. 201939838Z</div> : null}
      <div>Date: {formatInvoiceDate(order)}</div>
      <div className="whitespace-nowrap">
        {invoiceNoLabel} {order.invoiceNo}
      </div>
      {showTerms ? <div>Terms : C.O.D</div> : null}
    </div>
  );
};

export const InvoiceItemsTable = ({
  order,
  firstColumnLabel = 'NO',
  renderFirstCell,
}: {
  order: OrderDetailBatchReturnType;
  firstColumnLabel?: string;
  renderFirstCell?: (
    item: OrderDetailBatchReturnType['orderItems'][number],
    index: number,
  ) => React.ReactNode;
}) => {
  return (
    <table className="w-full border-collapse text-xs mb-[30px]">
      <thead>
        <tr>
          <th className="border border-black w-[48px] p-2 text-center font-bold">
            {firstColumnLabel}
          </th>
          <th className="border border-black py-2 text-center font-bold">
            Description
          </th>
          <th className="border border-black w-[190px] py-2 text-center font-bold">
            Unit price (S$)
          </th>
        </tr>
      </thead>
      <tbody>
        {order.orderItems.map((item, index) => (
          <tr key={item.id}>
            <td className="border border-black h-[42px] text-center align-middle">
              {renderFirstCell ? renderFirstCell(item, index) : index + 1}
            </td>
            <td className="border border-black px-3 py-2 align-middle">
              {item.productId}
              <br />
              {item.productName}
            </td>
            <td className="border border-black px-3 py-2 text-right align-middle">
              {formatAmount(item.productPriceActualCurrency)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export type TransferProductCost = {
  currency: string;
  costPrice: number;
};

const parseB2bProductIds = (b2bProductId?: string | null) =>
  (b2bProductId ?? '')
    .split(',')
    .map((id) => Number(id.trim()))
    .filter((id) => Number.isInteger(id) && id > 0);

/** 香港调货 B2B：按来源商品 ID 取调往新商品的币种进价 */
export const loadHkTransferCostBySourceProductId = async (
  orders: OrderDetailBatchReturnType[],
) => {
  const productIds = Array.from(
    new Set(
      orders
        .filter(
          (order) =>
            order.orderItems[0]?.product?.stockPlace ===
            STOCK_PLACE_MAP.HONGKONG,
        )
        .flatMap((order) => parseB2bProductIds(order.b2bProductId)),
    ),
  );

  const costBySourceProductId: Record<number, TransferProductCost> = {};
  if (!productIds.length) return costBySourceProductId;

  const products = await Promise.all(
    productIds.map((id) => getProductUpdateInfoNew(id)),
  );

  products.forEach((product) => {
    const costPrice = Number(product?.costPrice);
    if (!product?.sourceProductId || !Number.isFinite(costPrice)) return;
    costBySourceProductId[product.sourceProductId] = {
      currency: product.currency,
      costPrice,
    };
  });

  return costBySourceProductId;
};

/** 香港 Invoice 商品列表：Item / Description / QTY / 金额（表格样式与新加坡一致） */
export const HKInvoiceItemsTable = ({
  order,
  transferCostBySourceProductId,
}: {
  order: OrderDetailBatchReturnType;
  transferCostBySourceProductId?: Record<number, TransferProductCost>;
}) => {
  const hasTransferCost = order.orderItems.some(
    (item) => transferCostBySourceProductId?.[item.productId],
  );

  return (
    <table className="w-full border-collapse text-xs mb-[30px]">
      <thead>
        <tr>
          <th className="border border-black w-[88px] p-2 text-center font-bold">
            Item
          </th>
          <th className="border border-black py-2 text-center font-bold">
            Description
          </th>
          <th className="border border-black w-[56px] py-2 text-center font-bold">
            QTY
          </th>
          <th
            className={`border border-black py-2 text-center font-bold ${
              hasTransferCost ? 'w-[156px]' : 'w-[120px]'
            }`}
          >
            Unit price (HK$)
          </th>
        </tr>
      </thead>
      <tbody>
        {order.orderItems.map((item) => {
          const transferCost = transferCostBySourceProductId?.[item.productId];

          return (
            <tr key={item.id}>
              <td className="border border-black h-[42px] text-center align-middle">
                {item.productId}
              </td>
              <td className="border border-black px-3 py-2 align-middle">
                {item.productName}
              </td>
              <td className="border border-black px-3 py-2 text-center align-middle tabular-nums">
                {item.productQuantity}
              </td>
              <td className="border border-black px-3 py-2 text-right align-middle tabular-nums">
                <div>{formatAmount(item.productPriceActualCurrency)}</div>
                {transferCost ? (
                  <div className="mt-0.5 whitespace-nowrap">
                    {transferCost.currency}{' '}
                    {formatAmount(transferCost.costPrice)}
                  </div>
                ) : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export const ASUInvoiceHeader = ({ title }: { title: string }) => {
  return (
    <header className="mb-[30px] text-center">
      <p className="text-lg font-bold mb-1.5">ASU COMPANY PTE. LTD.</p>
      <p className="text-xs mb-1.5">Co Reg. No. 201939838Z</p>
      <p className="text-xs mb-1.5">
        391B Orchard Road #23-05 Ngee Ann City Singapore 238874
      </p>
      <p className="text-xs mb-4">Tel: +65 6530 3529 Fax: +65 65303528</p>
      <p className="text-[28px] font-bold">{title}</p>
    </header>
  );
};

export const HKInvoiceHeader = ({ title }: { title: string }) => {
  return (
    <header className="mb-[30px] text-center">
      <p className="text-lg font-bold mb-1.5">ASU COMPANY LIMITED</p>
      <p className="text-xs mb-1.5">
        Room1703-5, 17/F, New World Tower 1, 16-18 Queen&apos;s Road Central,
        Central, Hong Kong
      </p>
      <p className="text-xs mb-4">Tel: +852 2662 9721</p>
      <p className="text-[28px] font-bold">{title}</p>
    </header>
  );
};

export const HKInvoiceFooter = () => {
  return (
    <>
      <div className="mb-[22px]">
        <p className="font-bold mb-2">Bank Transfer to</p>
        <p className="pl-2">
          Beneficiary: ASU Company Limited
          <br />
          Account No: 817-336589-838
          <br />
          Bank: HSBC (THE HONGKONG AND SHANGHAI BANKING CORPORATION LIMITED)
          <br />
          Swift code: HSBCHKHHHKH
          <br />
          Bank code: 004
          <br />
          Bank address: 1 Queen&apos;s Road Central, Central, Hong Kong
        </p>
      </div>

      <div>
        <p className="font-bold mb-2">
          FPS (轉數快) - HK Local transfer use Only
        </p>
        <p className="pl-2">FPS ID: 166644419</p>
      </div>
    </>
  );
};

export const ASUInvoiceFooter = () => {
  return (
    <>
      <div className="mb-[22px]">
        <p className="font-bold mb-2">Bank Transfer to</p>
        <p className="pl-2">
          Beneficiary: ASU Company Pte. Ltd.
          <br />
          Account No: 601-798325-001
          <br />
          Bank: OCBC Bank
        </p>
      </div>

      <div>
        <p className="font-bold mb-2">For payment via Corporate Paynow</p>
        <p className="pl-2">
          - Enter PayNow ID 201939838ZOCB into the Unique Entity Number (UEN)
          field
        </p>
      </div>
    </>
  );
};
